<script setup lang="ts">
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
import { useDefaultStore } from "@/store";

;

type TMoviesRouteName =
	| "MoviesClassicGamePage"
	| "MoviesBeatTheClockPage"
	| null;

type TMoviesGameMode = {
	title: string;
	description: string;
	icon: string;
	routeName: TMoviesRouteName;
	disabled: boolean;
	isMultiplayer?: boolean;
	scores?: {
		best: number;
		last: number;
	};
};

const store = useDefaultStore();

const classicBestScore = computed(
	() => store.userData?.usr_movies_classic_best_score ?? 0,
);
const classicLastScore = computed(
	() => store.userData?.usr_movies_classic_last_score ?? 0,
);
const btcBestScore = computed(
	() => store.userData?.usr_movies_btc_best_score ?? 0,
);
const btcLastScore = computed(
	() => store.userData?.usr_movies_btc_last_score ?? 0,
);

const gameModes = computed<TMoviesGameMode[]>(() => [
	{
		title: "Classic",
		description: "Play a standard round and see how far you can go.",
		icon: "🎬",
		routeName: "MoviesClassicGamePage",
		disabled: false,
		scores: {
			best: classicBestScore.value,
			last: classicLastScore.value,
		},
	},
	{
		title: "Beat the Clock",
		description: "Answer quickly before time runs out.",
		icon: "⏱️",
		routeName: "MoviesBeatTheClockPage",
		disabled: false,
		scores: {
			best: btcBestScore.value,
			last: btcLastScore.value,
		},
	},
	{
		title: "Play with Others",
		description: "Challenge friends and compete together.",
		icon: "👥",
		routeName: null,
		disabled: false,
		isMultiplayer: true,
	},
]);

useHead({
	title: getPageTitle("Movies"),
	meta: [
		{
			name: "title",
			content: getPageTitle("Movies"),
		},
		{
			name: "description",
			content:
				"Challenge your movie knowledge with quick comparison games based on release years.",
		},
		{ name: "robots", content: "index, follow" },
		{
			property: "og:title",
			content: getPageTitle("Movies"),
		},
		{
			property: "og:description",
			content:
				"Challenge your movie knowledge with quick comparison games based on release years.",
		},
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
});
</script>

<template>
	<CategoryPageComp
		:category-code="store.MAIN_CATEGORIES.MOVIES"
		category-name="Movies"
		fallback-route-name="MoviesPage"
		:game-modes="gameModes"
		route-target-settings="MoviesSettingsPage"
		route-target-leaderboard="MoviesLeaderboard"
	/>
</template>
